import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { getData, postData } from "../../../backend/api";
import { decryptData, encryptData } from "../../functions/cryptoUtils";

// Styled Components
const Container = styled.div`
  // max-width: 600px;
  width: 100%;
  margin: 0 20px;
  padding: 20px;
  font-size: 16px;
  background-color: #f2f4fa;
`;

const CommentBox = styled.div`
  border: 1px solid #ccc;
  padding: 15px;
  margin-bottom: 15px;
  border-radius: 8px;
  background-color: #f9f9f9;
  display: inline-block;
  max-width: 80%; // Adjust this value as needed
`;

const CommentHeader = styled.div`
  font-weight: bold;
  margin-bottom: 10px;
  color: #333;
`;

const CommentText = styled.p`
  margin: 0;
  color: #555;
  word-wrap: break-word;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  margin-bottom: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const TextArea = styled.textarea`
  width: calc(100% - 20px);
  padding: 10px;
  margin-bottom: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  resize: vertical;
  font-size: 18px;
  font-family: "Calibri",-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
`;

const Button = styled.button`
  padding: 10px 20px;
  background-color: ${(props) => props.color};
  color: white;
  border: none;
  border-radius: ${(props) => (props.corner ? props.corner : "10px")};
  cursor: pointer;
  font-family: "Calibri",-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;

  &:hover {
    background-color: ${(props) =>  props.color};
  }
`;

const Status = styled.div`
  font-weight: bold;
  margin-bottom: 20px;
  color: ${(props) => (props.status === "Resolved" ? "green" : "red")};
`;

// Main Component
const WhistleblowingComments = ({ isAdmin=false, t, data , setMessage, channel, caseKey, accessHash}) => {
  const [comments, setComments] = useState([]); // Initialize empty
  const [newComment, setNewComment] = useState("");
  const [status, setStatus] = useState(data?.status || "open");
  
  // [NEW] We need to wait for the Key before we can process anything
  const getComments = async () => {
    // If we don't have the key or the ID yet, don't fetch
    if (!caseKey || !data?._id) return;

    try {
      let payloadData = { whistleblowingId: data._id };

      const headers = {
          'x-submitter-token': accessHash // PROOF OF IDENTITY
        };
      
      const response = await getData(payloadData, "whistleblowing-comment", null, null, headers);

      if (response.status === 200) {
        const rawComments = response?.data?.data || [];
        
        // [NEW] Decrypt all comments in parallel
        const decryptedList = await Promise.all(
          rawComments.map(async (item) => {
            try {
              // Decrypt the comment text
              const plainText = await decryptData(item.comment, caseKey);
              return { ...item, comment: plainText };
            } catch (err) {
              console.error("Failed to decrypt comment:", err);
              return { ...item, comment: "⚠️ Decryption Error" };
            }
          })
        );

        setComments(decryptedList);
      } 
    } catch (error) {
       console.error(error);
    }
  }

  // [UPDATE] Dependency Array: Run when data OR Key changes
  useEffect(() => {
    getComments();
  }, [data?._id, caseKey]); 
  

  const handleAddComment = async () => {
    if (newComment.trim()) {
        
        try {
            // [NEW] Encrypt the comment before sending
            // We use the same 'caseKey'
            if (!caseKey) {
                setMessage({ type: 1, content: "Security Key missing. Cannot send.", proceed: "Okay" });
                return;
            }

            const encryptedComment = await encryptData(newComment, caseKey);

            let payload = {
              comment: encryptedComment, // Send the Blob, not text
              whistleblowingId: data._id
            }

            const headers = {
              'x-submitter-token': accessHash // PROOF OF IDENTITY
            };
            
            const response = await postData(payload, `whistleblowing-comment/whistleblower/${data.caseId}`, null, null, headers);

            if (response.status === 200) {
              let res = { ...response?.data?.data };
              
              // We manually set the plain text for the UI so we don't have to re-fetch/re-decrypt immediately
              res.comment = newComment; 
              res.user = null; // Whistleblower has no user object usually

              setComments([...comments, res]);
              setNewComment("");
            } else {
               setMessage({ type: 1, content: t(response.data), proceed: "Okay" });
            }

        } catch (error) {
           console.error("Encryption failed:", error);
           setMessage({ type: 1, content: "Encryption failed.", proceed: "Okay" });
        }
    }
  }

  const handleStatusChange = (newStatus) => {
    setStatus(newStatus)
  }

  return (
    <Container>
      {/* <h1>Whistleblowing Comments</h1> */}
      {/* <Status status={status}>{t("status")}: {t(status)}</Status> */}

      {isAdmin && (
        <div>
          <Button onClick={() => handleStatusChange("In Progress")}>
            Mark as In Progress
          </Button>
          <Button onClick={() => handleStatusChange("Resolved")}>
            Mark as Resolved
          </Button>
        </div>
      )}

      <div>
        {comments.map((item,index) => (
          <div key={index} style={{display:'flex', justifyContent: item?.user?._id ? 'flex-start' : 'flex-end' }}>
            <CommentBox>
              <CommentHeader>
                { channel?.showCaseHandlerNameInMessages ? item?.user?.fullName ? item?.user?.fullName : t("you") : item?.user?.fullName ? t("Case handler") : t("you")} - {item.createdAt ? new Date(item.createdAt).toLocaleString("de-DE", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        }) : ""}
              </CommentHeader>
              <CommentText>{item.comment}</CommentText>
            </CommentBox>
          </div>
        ))}
      </div>

      <div>
        <TextArea
          disabled={channel?.disableReporterMsgAftClosing && data.status==="close"}
          rows="4"
          placeholder={t("addAComment")}
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
        />
        <Button disabled={channel?.disableReporterMsgAftClosing && data.status==="close"} color={channel?.primaryColor} corner={channel?.buttonCorners} style={{fontSize:"18px"}} onClick={handleAddComment}>{t("Add Comment")}</Button>
      </div>
    </Container>
  );
};

export default WhistleblowingComments;